import { useCallback, useEffect, useRef } from 'react';

/**
 * Universal hook for postMessage communication with an iframe.
 *
 * @param {Object} handlers - Map of { [eventType]: (eventData) => void }
 * @param {React.RefObject} iframeRef - Ref attached to the iframe element
 * @param {string} [iframeOrigin] - Expected origin URL for security validation
 * @returns {{ postMessage: (eventType: string, eventData: any) => void }}
 */
export const useIframePostMessage = (handlers, iframeRef, iframeOrigin) => {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const handleMessage = event => {
      if (iframeOrigin) {
        try {
          if (event.origin !== new URL(iframeOrigin).origin) return;
        } catch {
          return;
        }
      }

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data?.eventType && handlersRef.current[data.eventType]) {
          handlersRef.current[data.eventType](data.eventData);
        }
      } catch {
        // Ignore messages that don't match expected format
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeOrigin]);

  const targetOrigin = (() => {
    try {
      return iframeOrigin ? new URL(iframeOrigin).origin : '*';
    } catch {
      return '*';
    }
  })();

  const postMessage = useCallback(
    (eventType, eventData) => {
      if (!iframeRef?.current?.contentWindow) return;

      const message = { eventData, eventType };
      iframeRef.current.contentWindow.postMessage(message, targetOrigin);
    },
    [iframeRef, targetOrigin],
  );

  return { postMessage };
};
